/**
 * Users API
 * Generic user listing endpoint for targeting users in various features
 * NOTE: Reuses the /push/users endpoint with has_token=false for all users
 */

import apiClient from './client';
import type {
  UsersListResponse,
  AdminUserListResponse,
  AdminUserDetail,
  AdminUserListParams,
  BetaTesterStats,
  UserStats,
  UserRoleLevel,
  UserCompleteView,
  UserCompleteListResponse,
  UserCompleteListParams,
} from '@/types/users';

export interface UsersQueryParams {
  search?: string;
  has_token?: boolean;  // true = only users with push tokens, false = all users
  page?: number;
  limit?: number;
}

export const usersApi = {
  /**
   * Get users for targeting (push notifications, workflows)
   * @param params.search - Search by name or user_id
   * @param params.has_token - Only return users with active push tokens (default: false for workflow)
   * @param params.page - Page number (1-based, default: 1)
   * @param params.limit - Items per page (default: 50, max: 100)
   */
  getUsers: async (params?: UsersQueryParams): Promise<UsersListResponse> => {
    // Use push/users endpoint which supports has_token filter
    const queryParams = {
      ...params,
      has_token: params?.has_token ?? false,  // Default to all users
    };
    const response = await apiClient.get('/push/users', { params: queryParams });
    return response.data;
  },

  // ============================================================================
  // Admin User Management (Complete View - Recommended)
  // ============================================================================

  /**
   * List users with complete information from users_complete_view.
   * This includes profile, preference, and birth chart data in a single query.
   */
  listUsersComplete: async (params: UserCompleteListParams = {}): Promise<UserCompleteListResponse> => {
    const response = await apiClient.get('/users/complete', { params });
    return response.data;
  },

  /**
   * Get complete user details by ID from users_complete_view.
   */
  getUserComplete: async (userId: string): Promise<UserCompleteView> => {
    const response = await apiClient.get(`/users/complete/${userId}`);
    return response.data;
  },

  // ============================================================================
  // Admin User Management (Legacy - Backward Compatible)
  // ============================================================================

  /**
   * List users with pagination and filtering (admin)
   * @deprecated Use listUsersComplete for complete user information
   */
  listUsers: async (params: AdminUserListParams = {}): Promise<AdminUserListResponse> => {
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  /**
   * Get user details by ID (admin)
   * @deprecated Use getUserComplete for complete user information
   */
  getUser: async (userId: string): Promise<AdminUserDetail> => {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },

  /**
   * Get beta tester statistics
   */
  getBetaTesterStats: async (): Promise<BetaTesterStats> => {
    const response = await apiClient.get('/users/beta-testers/stats');
    return response.data;
  },

  /**
   * Update user's beta tester status
   */
  updateBetaTesterStatus: async (userId: string, isBetaTester: boolean): Promise<void> => {
    await apiClient.put(`/users/${userId}/beta-tester`, { is_beta_tester: isBetaTester });
  },

  /**
   * Batch update beta tester status
   */
  batchUpdateBetaTesters: async (userIds: string[], isBetaTester: boolean): Promise<{ updated_count: number }> => {
    const response = await apiClient.post('/users/beta-testers/batch', {
      user_ids: userIds,
      is_beta_tester: isBetaTester,
    });
    return response.data;
  },

  /**
   * List beta testers (convenience method)
   */
  listBetaTesters: async (params: { page?: number; page_size?: number } = {}): Promise<AdminUserListResponse> => {
    const response = await apiClient.get('/users/beta-testers/list', { params });
    return response.data;
  },

  // ============================================================================
  // Admin & Role Management
  // ============================================================================

  /**
   * Get comprehensive user statistics
   */
  getUserStats: async (): Promise<UserStats> => {
    const response = await apiClient.get('/users/stats');
    return response.data;
  },

  /**
   * Update user's admin status
   */
  updateAdminStatus: async (userId: string, isAdmin: boolean): Promise<void> => {
    await apiClient.put(`/users/${userId}/admin`, { is_admin: isAdmin });
  },

  /**
   * Update user's role level
   */
  updateUserRole: async (userId: string, roleLevel: UserRoleLevel): Promise<void> => {
    await apiClient.put(`/users/${userId}/role`, { role_level: roleLevel });
  },

  /**
   * Update residence location on a user's astro profile (birth chart).
   * Uses the birth-charts API with the astro_profile_id.
   */
  updateResidenceLocation: async (profileId: string, residenceLocation: string): Promise<void> => {
    await apiClient.put(`/birth-charts/${profileId}`, {
      residence_location: residenceLocation,
    });
  },
};
